import { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import { GridColDef } from "../../Unknown/DataGrid";
import useCenterServiceTranslations from "../../CenterService/CenterServicesAppointmentPreview/useTranslations";
import useTranslations from "./useTranslations";
import getColumnAlert from "./columns/getColumnAlert";
import getColumnStatus from "./columns/getColumnStatus";
import getColumnPatientName from "./columns/getColumnPatientName";
import getColumnLocation from "./columns/getColumnLocation";
import getColumnOffering from "./columns/getColumnOffering";
import { AppointmentColumnItem, AppointmentRowItem } from "./types";
import getColumnBtnOptions from "./columns/getColumnBtnOptions";
import getColumnTime from "./columns/getColumnTime";
import getColumnBtnView from "./columns/getColumnBtnView";
import useStyles from "./useStyles";
import getColumnFullOfferings from "./columns/getColumnFullOfferings";
import getColumnFullPackages from "./columns/getColumnFullPackages";
import getColumnFullAddOns from "./columns/getColumnFullAddOns";
import getColumnCustomer from "./columns/getColumnCustomer";
import getColumnPayments from "./columns/getColumnPayments";
import getColumnBookingScenario from "./columns/getColumnBookingScenario";
import getColumnBookedBy from "./columns/getColumnBookedBy";
import getColumnCreatedAt from "./columns/getColumnCreatedAt";
import getColumnPatientAddress from "./columns/getColumnPatientAddress";
import getColumnPatientEmail from "./columns/getColumnPatientEmail";
import getColumnDate from "./columns/getColumnDate";
import getColumnLocationState from "./columns/getColumnLocationState";
import getColumnLocationZip from "./columns/getColumnLocationZip";
import getColumnPatientZip from "./columns/getColumnPatientZip";
import getColumnPatientState from "./columns/getColumnPatientState";
import getColumnBookedDate from "./columns/getColumnBookedDate";
import getColumnBookedTime from "./columns/getColumnBookedTime";
import getColumnIsPast from "./columns/getColumnIsPast";
import getColumnFlags from "./columns/getColumnFlags";
import getColumnPatientAge from "./columns/getColumnPatientAge";
import getColumnPatientDOB from "./columns/getColumnPatientDOB";
import getColumnPatientPhone from "./columns/getColumnPatientPhone";
import getColumnPatientAddressLine1 from "./columns/getColumnPatientAddressLine1";
import getColumnPatientAddressLine2 from "./columns/getColumnPatientAddressLine2";
import getColumnPatientCity from "./columns/getColumnPatientCity";
import getColumnPatientId from "./columns/getColumnPatientId";
import getColumnPatientRace from "./columns/getColumnPatientRace";
import getColumnPatientEthnicity from "./columns/getColumnPatientEthnicity";
import getColumnPatientSex from "./columns/getColumnPatientSex";
import { useRaceTranslations } from "../../Patient/PatientEditForm/useRaceOptions";
import { useEthnicityTranslations } from "../../Patient/PatientEditForm/useEthnicityOptions";
import { useSexTranslations } from "../../Patient/PatientEditForm/useSexOptions";
import getColumnAppointmentTotal from "./columns/getColumnAppointmentTotal";
import getColumnCenterServices from "./columns/getColumnCenterServices";
import getColumnAddOns from "./columns/getColumnAddOns";
import getColumnPatient from "./columns/getColumnPatient";
import getColumnCoupon from "./columns/getColumnCoupon";

export type DashboardColumnsParams = {
  columnItems: AppointmentColumnItem[];
};

const SORTABLE_FILTERABLE_FIELDS = new Set([
  "status",
  "date",
  "time",
  "location",
  "locationState",
  "locationZip",
]);

const STATUS_TRANSLATION_KEYS: Record<string, string> = {
  checkout: "statusCheckout",
  incomplete: "statusIncomplete",
  abandoned: "statusAbandoned",
  confirmed: "statusConfirmed",
  canceled: "statusCanceled",
  intake_complete: "statusIntakeComplete",
  checked_in: "statusCheckedIn",
  no_show: "statusNoShow",
  studies_unread: "statusStudiesUnread",
  studies_read: "statusStudiesRead",
  reports_sent: "statusReportsSent",
  reports_error: "statusReportsError",
};

const useDashboardColumns = (params: DashboardColumnsParams) => {
  const { columnTranslations } = useTranslations();
  const centerServiceTranslations = useCenterServiceTranslations();
  const { hideRightSeparatorRoot, hideHeaderTextRoot } = useStyles();
  const navigate = useNavigate();
  const { role } = useParams();
  const raceTranslations = useRaceTranslations();
  const ethnicityTranslations = useEthnicityTranslations();
  const sexTranslations = useSexTranslations();

  const { formatNumber } = useIntl();

  const columns = useMemo((): GridColDef<AppointmentRowItem>[] => {
    const { columnItems } = params;

    const formatAmount = (price: number, currencyCode: string) => {
      return formatNumber(price, {
        style: "currency",
        currency: currencyCode,
      }).replace(".00", "");
    };

    const innerColumns = columnItems.map((columnItem) => {
      let column: GridColDef<AppointmentRowItem>;

      switch (columnItem.field) {
        case "alert": {
          column = getColumnAlert({
            columnItem,
            translations: {
              columnAlert: columnTranslations.columnAlert,
              tooltipStat: columnTranslations.tooltipStat,
              tooltipAdditionalViews: columnTranslations.tooltipAdditionalViews,
              tooltipUnexpectedFindings:
                columnTranslations.tooltipUnexpectedFindings,
              tooltipReceiptConfirmed:
                columnTranslations.tooltipReceiptConfirmed,
              tooltipHasPriority: columnTranslations.tooltipHasPriority,
              tooltipRequiresGuardian:
                columnTranslations.tooltipRequiresGuardian,
              tooltipRequiresContactingPatient:
                columnTranslations.tooltipRequiresContactingPatient,
              containsAny: columnTranslations.containsAny,
              containsAll: columnTranslations.containsAll,
              tooltipHasPreviousAppointments:
                columnTranslations.tooltipHasPreviousAppointments,
            },
            headerClassName: hideHeaderTextRoot,
          });
          break;
        }
        case "flags": {
          column = getColumnFlags({
            columnItem,
            translations: {
              columnAlert: columnTranslations.columnFlags,
              flagStat: columnTranslations.flagStat,
              flagAdditionalViews: columnTranslations.flagAdditionalViews,
              flagHasPriority: columnTranslations.flagHasPriority,
              flagRequiresContactingPatient:
                columnTranslations.tooltipRequiresContactingPatient,
              flagUnexpectedFindings: columnTranslations.flagUnexpectedFindings,
              flagRequiresGuardian: columnTranslations.flagRequiresGuardian,
              flagReceiptConfirmed: columnTranslations.flagReceiptConfirmed,
              containsAny: columnTranslations.containsAny,
              containsAll: columnTranslations.containsAll,
              flagHasPreviousAppointment:
                columnTranslations.tooltipHasPreviousAppointments,
            },
          });
          break;
        }
        case "status": {
          const valueOptions = columnItem.valueOptions.map((value) => {
            const translationKey = STATUS_TRANSLATION_KEYS[value];
            const label = translationKey
              ? (columnTranslations as Record<string, string>)[
                  translationKey
                ] ?? value
              : value;
            return { value, label };
          });
          column = getColumnStatus({
            columnItem,
            translations: {
              columnStatus: columnTranslations.columnStatus,
              statusCheckout: columnTranslations.statusCheckout,
              statusIncomplete: columnTranslations.statusIncomplete,
              statusAbandoned: columnTranslations.statusAbandoned,
              statusConfirmed: columnTranslations.statusConfirmed,
              statusCanceled: columnTranslations.statusCanceled,
              statusIntakeComplete: columnTranslations.statusIntakeComplete,
              statusCheckedIn: columnTranslations.statusCheckedIn,
              statusNoShow: columnTranslations.statusNoShow,
              statusReportsError: columnTranslations.statusReportsError,
              statusReportsSent: columnTranslations.statusReportsSent,
              statusStudiesRead: columnTranslations.statusStudiesRead,
              statusStudiesUnread: columnTranslations.statusStudiesUnread,
            },
            valueOptions,
          });
          break;
        }
        case "patient": {
          column = getColumnPatient({
            columnItem,
            translations: {
              columnPatient: columnTranslations.columnPatient,
              years: columnTranslations.years,
            },
            onNameClick: (id: string) =>
              navigate(`/${role}/dashboard/patient/${id}`),
          });
          break;
        }
        case "patientName": {
          column = getColumnPatientName({
            columnItem,
            translations: {
              columnPatientName: columnTranslations.columnPatientName,
            },
            onNameClick: (id: string) =>
              navigate(`/${role}/dashboard/patient/${id}`),
          });
          break;
        }
        case "patientAge": {
          column = getColumnPatientAge({
            columnItem,
            translations: {
              columnPatientAge: columnTranslations.columnPatientAge,
            },
          });
          break;
        }
        case "appointmentTotal": {
          column = getColumnAppointmentTotal({
            columnItem,
            translations: {
              columnAppointmentTotal: columnTranslations.columnAppointmentTotal,
            },
          });
          break;
        }
        case "patientDOB": {
          column = getColumnPatientDOB({
            columnItem,
            translations: {
              columnPatientDOB: columnTranslations.columnPatientDOB,
            },
          });
          break;
        }
        case "patientId": {
          column = getColumnPatientId({
            columnItem,
            translations: {
              columnPatientId: columnTranslations.columnPatientId,
            },
          });
          break;
        }
        case "patientRace": {
          column = getColumnPatientRace({
            columnItem,
            translations: {
              columnPatientRace: columnTranslations.columnPatientRace,
              raceOptions: raceTranslations,
            },
          });
          break;
        }
        case "patientEthnicity": {
          column = getColumnPatientEthnicity({
            columnItem,
            translations: {
              columnPatientEthnicity: columnTranslations.columnPatientEthnicity,
              ethnicityOptions: ethnicityTranslations,
            },
          });
          break;
        }
        case "patientSex": {
          column = getColumnPatientSex({
            columnItem,
            translations: {
              columnPatientSex: columnTranslations.columnPatientSex,
              sexOptions: sexTranslations,
            },
          });
          break;
        }
        case "patientPhone": {
          column = getColumnPatientPhone({
            columnItem,
            translations: {
              columnPatientPhone: columnTranslations.columnPatientPhone,
            },
          });
          break;
        }
        case "date": {
          column = getColumnDate({
            columnItem,
            translations: {
              columnDate: columnTranslations.columnDate,
            },
          });
          break;
        }
        case "time": {
          column = getColumnTime({
            columnItem,
            translations: {
              columnTime: columnTranslations.columnTime,
            },
          });
          break;
        }
        case "bookedDate": {
          column = getColumnBookedDate({
            columnItem,
            translations: {
              columnBookedDate: columnTranslations.columnBookedDate,
            },
          });
          break;
        }
        case "bookedTime": {
          column = getColumnBookedTime({
            columnItem,
            translations: {
              columnBookedTime: columnTranslations.columnBookedTime,
            },
          });
          break;
        }
        case "location": {
          column = getColumnLocation({
            columnItem,
            translations: {
              columnLocation: columnTranslations.columnLocation,
            },
          });
          break;
        }
        case "locationState": {
          column = getColumnLocationState({
            columnItem,
            translations: {
              columnLocationState: columnTranslations.columnLocationState,
            },
          });
          break;
        }
        case "locationZip": {
          column = getColumnLocationZip({
            columnItem,
            translations: {
              columnLocationZip: columnTranslations.columnLocationZip,
            },
          });
          break;
        }
        case "offering": {
          column = getColumnOffering({
            columnItem,
            translations: {
              columnOffering: columnTranslations.columnOffering,
              columnOfferingStudies: columnTranslations.columnOfferingStudies,
            },
            centerServiceTranslations,
          });
          break;
        }
        case "centerServices": {
          column = getColumnCenterServices({
            columnItem,
            translations: {
              columnOffering: columnTranslations.columnOffering,
            },
            centerServiceTranslations,
          });
          break;
        }
        case "btnOptions": {
          column = getColumnBtnOptions({
            columnItem,
            translations: {
              columnBtnOptions: columnTranslations.columnBtnOptions,
              optionsBtnCheckIn: columnTranslations.optionsBtnCheckIn,
              optionsBtnLogIn: columnTranslations.optionsBtnLogIn,
            },
            headerClassName: `${hideRightSeparatorRoot} ${hideHeaderTextRoot}`,
          });
          break;
        }
        case "btnView": {
          column = getColumnBtnView({
            columnItem,
            translations: {
              columnBtnView: columnTranslations.columnBtnView,
              btnView: columnTranslations.btnView,
            },
            handleClick: (id) => navigate(`/${role}/appointment/${id}`),
            headerClassName: `${hideRightSeparatorRoot} ${hideHeaderTextRoot}`,
          });
          break;
        }
        case "fullOfferings": {
          column = getColumnFullOfferings({
            columnItem,
            translations: {
              columnFullOfferings: columnTranslations.columnFullOfferings,
              sideRight: columnTranslations.sideRight,
              sideLeft: columnTranslations.sideLeft,
            },
            formatAmount,
          });
          break;
        }
        case "fullPackages": {
          column = getColumnFullPackages({
            columnItem,
            translations: {
              columnFullPackages: columnTranslations.columnFullPackages,
            },
            formatAmount,
          });
          break;
        }
        case "addOns": {
          column = getColumnAddOns({
            columnItem,
            translations: {
              columnAddOns: columnTranslations.columnFullAddOns,
              for: columnTranslations.for,
              left: columnTranslations.left,
              right: columnTranslations.right,
            },
          });
          break;
        }
        case "fullAddOns": {
          column = getColumnFullAddOns({
            columnItem,
            translations: {
              columnFullAddOns: columnTranslations.columnFullAddOns,
            },
            formatAmount,
          });
          break;
        }
        case "customer": {
          column = getColumnCustomer({
            columnItem,
            translations: {
              columnCustomer: columnTranslations.columnCustomer,
            },
          });
          break;
        }
        case "payments": {
          column = getColumnPayments({
            columnItem,
            translations: {
              columnPayments: columnTranslations.columnPayments,
              providerStripe: columnTranslations.providerStripe,
              providerCash: columnTranslations.providerCash,
              providerUnknown: columnTranslations.providerUnknown,
            },
          });
          break;
        }
        case "bookingScenario": {
          column = getColumnBookingScenario({
            columnItem,
            translations: {
              columnBookingScenario: columnTranslations.columnBookingScenario,
              scenarioPatient: columnTranslations.scenarioPatient,
              scenarioLegalGuardian: columnTranslations.scenarioLegalGuardian,
              scenarioOtherPerson: columnTranslations.scenarioOtherPerson,
              scenarioFamilyMember: columnTranslations.scenarioFamilyMember,
            },
          });
          break;
        }
        case "bookedBy": {
          column = getColumnBookedBy({
            columnItem,
            translations: {
              columnBookedBy: columnTranslations.columnBookedBy,
            },
          });
          break;
        }
        case "createdAt": {
          column = getColumnCreatedAt({
            columnItem,
            translations: {
              columnCreatedAt: columnTranslations.columnCreatedAt,
            },
          });
          break;
        }
        case "patientAddress": {
          column = getColumnPatientAddress({
            columnItem,
            translations: {
              columnPatientAddress: columnTranslations.columnPatientAddress,
            },
          });
          break;
        }
        case "patientState": {
          column = getColumnPatientState({
            columnItem,
            translations: {
              columnPatientState: columnTranslations.columnPatientState,
            },
          });
          break;
        }
        case "patientZip": {
          column = getColumnPatientZip({
            columnItem,
            translations: {
              columnPatientZip: columnTranslations.columnPatientZip,
            },
          });
          break;
        }
        case "patientEmail": {
          column = getColumnPatientEmail({
            columnItem,
            translations: {
              columnPatientEmail: columnTranslations.columnPatientEmail,
            },
          });
          break;
        }
        case "patientAddressLine1": {
          column = getColumnPatientAddressLine1({
            columnItem,
            translations: {
              columnPatientAddressLine1:
                columnTranslations.columnPatientAddressLine1,
            },
          });
          break;
        }
        case "patientCity": {
          column = getColumnPatientCity({
            columnItem,
            translations: {
              columnPatientCity: columnTranslations.columnPatientCity,
            },
          });
          break;
        }
        case "patientAddressLine2": {
          column = getColumnPatientAddressLine2({
            columnItem,
            translations: {
              columnPatientAddressLine2:
                columnTranslations.columnPatientAddressLine2,
            },
          });
          break;
        }
        case "coupon": {
          column = getColumnCoupon({
            columnItem,
            translations: {
              columnCoupon: columnTranslations.columnCoupon,
            },
          });
          break;
        }
        case "isPast": {
          column = getColumnIsPast({
            columnItem,
          });
          break;
        }
        default: {
          throw new Error("Column is not defined");
        }
      }

      if (!SORTABLE_FILTERABLE_FIELDS.has(columnItem.field)) {
        return { ...column, sortable: false, filterable: false };
      }
      return column;
    });

    return innerColumns;
  }, [
    params,
    formatNumber,
    columnTranslations,
    hideHeaderTextRoot,
    navigate,
    role,
    raceTranslations,
    ethnicityTranslations,
    sexTranslations,
    centerServiceTranslations,
    hideRightSeparatorRoot,
  ]);
  return columns;
};

export default useDashboardColumns;
